import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  SUPPORTED_LANG_CODES,
  resolveLanguage,
  type SupportedLangCode,
} from '@/application/i18n/language'
import { DEFAULT_LOCALE } from '@/application/i18n/locale-path'
import { OG_LOCALES, localizedAbsoluteUrl } from '@/application/seo/site'

/**
 * Keeps `document.title` and the Open Graph / Twitter Card tags in sync with
 * the page currently rendered.
 *
 * This covers the browser tab and the crawlers that *do* execute JavaScript
 * (Googlebot). Social crawlers — Facebook, X, Discord, LinkedIn, Slack — never
 * run scripts: they are served a pre-rendered document by the backend
 * `share` endpoint instead (see `investigation-share.service.ts`).
 */

/** Open Graph descriptions are truncated by every platform well before this. */
export const META_DESCRIPTION_MAX_LENGTH = 200

/** Below this ratio a word-boundary cut would drop too much of the text. */
const MIN_WORD_BOUNDARY_RATIO = 0.6

export interface DocumentMeta {
  title: string
  description?: string
  imageUrl?: string | null
  url?: string
  siteName?: string
  type?: 'article' | 'website'
}

/**
 * Flattens authored rich text (Markdown, sometimes with inline HTML) into the
 * single plain sentence crawlers expect, then cuts it on a word boundary.
 */
export function summarize(rawText: string, maxLength = META_DESCRIPTION_MAX_LENGTH): string {
  const text = rawText
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/<[^>]*>/g, ' ')
    .replace(/[*_`~>#]/g, '')
    .replace(/\s+/g, ' ')
    .trim()

  if (text.length <= maxLength) return text

  const clipped = text.slice(0, maxLength)
  const lastSpace = clipped.lastIndexOf(' ')
  const cutText =
    lastSpace > maxLength * MIN_WORD_BOUNDARY_RATIO ? clipped.slice(0, lastSpace) : clipped
  return `${cutText.trimEnd()}…`
}

/** Marks the tags this hook created, so they can be removed on unmount. */
const OWNED_ATTRIBUTE = 'data-document-meta'

/**
 * Sets one meta tag, reusing the one already in `index.html` when it exists.
 * Returns the undo needed to leave the head exactly as it was found.
 */
function upsertMeta(attribute: 'name' | 'property', metaKey: string, value: string): () => void {
  const existing = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${metaKey}"]`)

  if (existing) {
    const previousContent = existing.getAttribute('content')
    existing.setAttribute('content', value)
    return () => {
      if (previousContent === null) existing.removeAttribute('content')
      else existing.setAttribute('content', previousContent)
    }
  }

  const created = document.createElement('meta')
  created.setAttribute(attribute, metaKey)
  created.setAttribute(OWNED_ATTRIBUTE, '')
  created.setAttribute('content', value)
  document.head.appendChild(created)
  return () => created.remove()
}

export function useDocumentMeta(meta: DocumentMeta | null): void {
  const { title, description, imageUrl, url, siteName, type } = meta ?? {}

  useEffect(() => {
    if (!title) return

    const previousTitle = document.title
    document.title = siteName ? `${title} · ${siteName}` : title

    const entries: Array<['name' | 'property', string, string]> = [
      ['property', 'og:type', type ?? 'article'],
      ['property', 'og:title', title],
      ['name', 'twitter:card', 'summary_large_image'],
      ['name', 'twitter:title', title],
    ]
    if (description) {
      entries.push(['name', 'description', description])
      entries.push(['property', 'og:description', description])
      entries.push(['name', 'twitter:description', description])
    }
    if (imageUrl) {
      entries.push(['property', 'og:image', imageUrl])
      entries.push(['name', 'twitter:image', imageUrl])
    }
    if (url) entries.push(['property', 'og:url', url])
    if (siteName) entries.push(['property', 'og:site_name', siteName])

    const undos = entries.map(([attribute, metaKey, value]) =>
      upsertMeta(attribute, metaKey, value),
    )

    return () => {
      document.title = previousTitle
      undos.forEach((undo) => undo())
    }
  }, [title, description, imageUrl, url, siteName, type])
}

/** Marks the `<link>` tags this hook created, so they can be removed on unmount. */
const OWNED_LINK_ATTRIBUTE = 'data-document-link'

/**
 * Sets one `<link>`, reusing the one already in `index.html` when it exists.
 *
 * Reuse is the point, not an optimisation: the static shell ships a
 * `<link rel="canonical">` so the pre-hydration document is already correct,
 * and appending a second one would leave the page advertising TWO canonicals —
 * which Google resolves by ignoring both. Returns the undo needed to leave the
 * head exactly as it was found.
 */
function upsertLink(relation: string, href: string, hreflang?: string): () => void {
  const selector = hreflang
    ? `link[rel="${relation}"][hreflang="${hreflang}"]`
    : `link[rel="${relation}"]:not([hreflang])`
  const existing = document.head.querySelector<HTMLLinkElement>(selector)

  if (existing) {
    const previousHref = existing.getAttribute('href')
    existing.setAttribute('href', href)
    return () => {
      if (previousHref === null) existing.removeAttribute('href')
      else existing.setAttribute('href', previousHref)
    }
  }

  const created = document.createElement('link')
  created.setAttribute('rel', relation)
  if (hreflang) created.setAttribute('hreflang', hreflang)
  created.setAttribute('href', href)
  created.setAttribute(OWNED_LINK_ATTRIBUTE, '')
  document.head.appendChild(created)
  return () => created.remove()
}

/**
 * Sets the nth `og:locale:alternate`.
 *
 * Open Graph allows the property more than once, but `upsertMeta` keys on the
 * property name and would keep rewriting the same tag, leaving only the last
 * language advertised. Indexing the lookup by document order lets the two
 * alternates coexist while still being reused (rather than duplicated) across
 * re-renders.
 */
function upsertOgLocaleAlternate(value: string, index: number): () => void {
  const existing = document.head.querySelectorAll<HTMLMetaElement>(
    'meta[property="og:locale:alternate"]',
  )
  const target = existing[index]

  if (target) {
    const previousContent = target.getAttribute('content')
    target.setAttribute('content', value)
    return () => {
      if (previousContent === null) target.removeAttribute('content')
      else target.setAttribute('content', previousContent)
    }
  }

  const created = document.createElement('meta')
  created.setAttribute('property', 'og:locale:alternate')
  created.setAttribute(OWNED_ATTRIBUTE, '')
  created.setAttribute('content', value)
  document.head.appendChild(created)
  return () => created.remove()
}

/**
 * Keeps `document.documentElement.lang` in sync with the active i18n
 * language, and publishes a canonical `<link>`, one `hreflang` alternate per
 * supported language (+ `x-default`), and the `og:locale` /
 * `og:locale:alternate` pair for the given path.
 *
 * The href is built from `SITE_URL` (via `localizedAbsoluteUrl`), NOT from
 * `window.location.origin`. The site answers on both `balivernologie.info`
 * and `www.balivernologie.info`, so a location-derived canonical would tell
 * Google that a page reached on the apex is canonically on the apex — exactly
 * the duplicate-content split the canonical tag exists to prevent. The
 * canonical host is a fixed property of the site, not of the request.
 *
 * Each language is served at its OWN URL (`/about`, `/en/about`, `/es/about`),
 * so the canonical points at the current language's URL and never across
 * locales, while the alternates enumerate all three plus `x-default` on the
 * unprefixed French URL. Every URL in that set is self-consistent: fetching
 * it really does return that language, and its own alternate set names the
 * same cluster back.
 *
 * `path` must be the LANGUAGE-FREE pathname (e.g. `/investigations`) — which
 * is exactly what the router reports, since the locale segment is stripped
 * before matching (see `i18n/locale-history.ts`). Pass `null` to skip
 * canonical/hreflang (e.g. while a dynamic route's data is still loading and
 * no stable URL is known yet).
 *
 * `availableLocales` gates which languages get advertised as hreflang
 * alternates / `og:locale:alternate`s: only locales in this list are emitted.
 * It defaults to every supported language, which is correct for the static
 * pages — their copy really is hand-written in all three. Content pages
 * (investigations, leads, facts) must pass only the locale(s) their content
 * is actually available in: advertising a locale you don't serve is worse
 * than not advertising it (see `TRANSLATION-PLAN.md`, Phase 0). The canonical
 * link and `og:locale` for the CURRENT locale are always published regardless
 * of `availableLocales` — a page still needs to canonicalise to itself even
 * while its content isn't translated yet.
 *
 * `pathsByLocale` is for pages whose URL differs by language (investigation
 * slugs): the canonical, each alternate and `x-default` use the path of THEIR
 * language, falling back to `path`. Static pages omit it.
 */
export function usePageLangAndHreflang(
  path: string | null,
  availableLocales: readonly SupportedLangCode[] = SUPPORTED_LANG_CODES,
  pathsByLocale?: Partial<Record<SupportedLangCode, string>>,
): void {
  const { i18n } = useTranslation()
  const locale = resolveLanguage(i18n.language)
  // Value-compared in the effect deps: callers build the record on each render.
  const pathsKey = pathsByLocale ? JSON.stringify(pathsByLocale) : ''

  useEffect(() => {
    const previousLang = document.documentElement.lang
    document.documentElement.lang = locale
    return () => {
      document.documentElement.lang = previousLang
    }
  }, [locale])

  useEffect(() => {
    if (!path) return

    const pathOf = (lang: SupportedLangCode): string => pathsByLocale?.[lang] ?? path

    const undos = [
      upsertLink('canonical', localizedAbsoluteUrl(pathOf(locale), locale)),
      ...availableLocales.map((lang) =>
        upsertLink('alternate', localizedAbsoluteUrl(pathOf(lang), lang), lang),
      ),
      ...(availableLocales.includes(DEFAULT_LOCALE)
        ? [
            upsertLink(
              'alternate',
              localizedAbsoluteUrl(pathOf(DEFAULT_LOCALE), DEFAULT_LOCALE),
              'x-default',
            ),
          ]
        : []),
      upsertMeta('property', 'og:locale', OG_LOCALES[locale]),
      ...availableLocales
        .filter((lang) => lang !== locale)
        .map((lang, index) => upsertOgLocaleAlternate(OG_LOCALES[lang], index)),
    ]

    return () => {
      undos.forEach((undo) => undo())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- availableLocales is a readonly array literal at each call site; comparing by reference would re-run every render for static-page callers, so it's intentionally excluded here in favour of `path`/`locale`/`pathsKey`.
  }, [path, locale, pathsKey])
}
